import { renderToStaticMarkup } from 'react-dom/server';
import type { LectureContent, LectureRecord } from '@wx-weiqi/contracts';
import { LectureDiagram } from './LectureDiagram';
import { lecturePositionLabel } from '../../domain/lecture-records';
const STYLE = `@page{size:A4 landscape;margin:0}*{box-sizing:border-box}body{margin:0;background:#e7e4dd;color:#2d342f;font-family:'PingFang SC','Microsoft YaHei',sans-serif}article{width:297mm;height:210mm;padding:9mm 12mm;background:#fffdf8;break-after:page;page-break-after:always;overflow:hidden;margin:0 auto 6mm;print-color-adjust:exact;-webkit-print-color-adjust:exact}article:last-child{break-after:auto;page-break-after:auto}header{height:22mm;display:flex;align-items:flex-start;justify-content:space-between;gap:8mm;border-bottom:1px solid #cfc7b7;margin-bottom:3mm}h1{font-size:15pt;margin:0;max-width:190mm;overflow-wrap:anywhere;line-height:1.35}header p{font-size:9pt;margin:1mm 0;color:#625f53}.body{display:grid;grid-template-columns:160mm 105mm;gap:8mm;height:160mm}.board{width:160mm;height:160mm}.text{height:160mm;overflow:hidden;font-size:11.5pt;line-height:19pt;overflow-wrap:anywhere;white-space:pre-wrap}.text p{margin:0 0 3mm;line-height:19pt}.text p.bullet{padding-left:4mm;text-indent:-4mm}footer{height:5mm;font-size:8pt;color:#686456;display:flex;justify-content:space-between;margin-top:2mm}@media screen{article{zoom:var(--preview-scale,1)}}@media print{body{background:white}article{margin:0;zoom:1}}`;
function renderRuns(doc: Document, p: HTMLElement, runs: LectureContent[number]['runs']) {
  p.replaceChildren(
    ...runs.map((run) => {
      const node = doc.createElement(run.bold ? 'strong' : 'span');
      node.textContent = run.text;
      return node;
    }),
  );
}
function sliceRuns(runs: LectureContent[number]['runs'], start: number, end: number) {
  let at = 0;
  return runs.flatMap((run) => {
    const chars = Array.from(run.text),
      from = Math.max(0, start - at),
      to = Math.min(chars.length, end - at);
    at += chars.length;
    return from < to ? [{ text: chars.slice(from, to).join(''), bold: run.bold }] : [];
  });
}
/** Measure the actual print font and split overflowing paragraphs without dropping a character. */
export async function renderLecturePrint(doc: Document, records: LectureRecord[]) {
  doc.open();
  doc.write(
    `<!doctype html><html lang="zh-CN"><head><meta charset="utf-8"><title>讲解记录</title><style>${STYLE}</style></head><body></body></html>`,
  );
  doc.close();
  await doc.fonts.ready;
  doc.documentElement.style.setProperty(
    '--preview-scale',
    String(Math.min(1, doc.documentElement.clientWidth / ((297 * 96) / 25.4))),
  );
  for (const record of records) {
    let page = 0;
    let column: HTMLElement;
    const newPage = () => {
      page++;
      const article = doc.createElement('article');
      article.innerHTML = renderToStaticMarkup(
        <>
          <header>
            <div>
              <h1 style={record.title.length > 55 ? { fontSize: '11.5pt' } : undefined}>
                {record.title || `${lecturePositionLabel(record.snapshot)}讲解`}
              </h1>
              <p>
                {lecturePositionLabel(record.snapshot)} ·{' '}
                {record.snapshot.nextColor === 'BLACK' ? '黑先' : '白先'}
                {page > 1 ? ' · 续页' : ''}
              </p>
            </div>
            <p>讲解记录</p>
          </header>
          <div className="body">
            <div className="board">
              <LectureDiagram snapshot={record.snapshot} id={`print-${record.recordId}-${page}`} />
            </div>
            <div className="text" />
          </div>
          <footer>
            <span>
              {record.snapshot.source.name.slice(0, 65)} · 版本 {record.version}
            </span>
            <span className="page-number" />
          </footer>
        </>,
      );
      doc.body.append(article);
      column = article.querySelector('.text')!;
    };
    newPage();
    for (const block of record.content) {
      const length = block.runs.reduce((n, r) => n + Array.from(r.text).length, 0);
      let from = 0;
      do {
        const p = doc.createElement('p');
        p.className = block.kind === 'BULLET' ? 'bullet' : '';
        column!.append(p);
        const put = (to: number) =>
          renderRuns(doc, p, [
            ...(block.kind === 'BULLET' ? [{ text: from === 0 ? '• ' : '  ', bold: false }] : []),
            ...sliceRuns(block.runs, from, to),
            ...(length === 0 ? [{ text: '\u00a0', bold: false }] : []),
          ]);
        put(length);
        if (column!.scrollHeight <= column!.clientHeight + 1) {
          break;
        }
        let low = from,
          high = length;
        while (low < high) {
          const mid = Math.ceil((low + high) / 2);
          put(mid);
          if (column!.scrollHeight <= column!.clientHeight + 1) low = mid;
          else high = mid - 1;
        }
        if (low === from) {
          p.remove();
          if (column!.children.length === 0)
            throw new Error('打印字体无法排版，请调整浏览器缩放后重试。');
        } else {
          put(low);
          from = low;
        }
        if (doc.body.children.length >= 500) throw new Error('导出页数过多，请减少所选记录。');
        newPage();
      } while (from < length);
    }
  }
  const pages = [...doc.querySelectorAll('.page-number')];
  pages.forEach((el, i) => {
    el.textContent = `${i + 1} / ${pages.length}`;
  });
  return pages.length;
}
